import { useEffect, useMemo } from 'react';
import type { CanonicalQuestion, MediaRef, Option } from '../../core/model/canonical.ts';
import type { OptionId } from '../../core/model/brands.ts';
import type { Response } from '../../core/model/session.ts';
import { useI18n } from '../../i18n/index.tsx';
import { ExamText } from '../../ui/primitives.tsx';

/**
 * The display letter for a position.
 *
 * Computed from where an option is **shown**, never stored — which is the whole reason option ids
 * are opaque. After a shuffle, "C" means the third thing on this screen, and nothing else.
 */
export function letterFor(index: number): string {
  return String.fromCharCode(65 + (index % 26));
}

/** Reorders options to match the blueprint, leaving them as authored when it has no opinion. */
export function orderOptions(
  options: readonly Option[],
  order: readonly string[] | undefined,
): readonly Option[] {
  if (order === undefined) {
    return options;
  }
  const byId = new Map(options.map((option) => [option.id as string, option]));
  const ordered = order.map((id) => byId.get(id)).filter((option): option is Option => option !== undefined);
  // Anything the blueprint did not mention still has to be shown; dropping an option silently
  // would change the question.
  const missing = options.filter((option) => !order.includes(option.id));
  return [...ordered, ...missing];
}

export function QuestionPrompt({ question }: { readonly question: CanonicalQuestion }) {
  return (
    <div className="question__prompt">
      <ExamText as="div" lang={question.content.language}>
        {question.content.prompt}
      </ExamText>

      {question.content.instructions !== undefined && (
        <ExamText lang={question.content.language} className="question__instructions">
          {question.content.instructions}
        </ExamText>
      )}

      {question.content.media.map((media) => (
        <Media key={media.id} media={media} lang={question.content.language} />
      ))}
    </div>
  );
}

/**
 * An image, or an honest account of the one that is missing.
 *
 * The missing case is the common one and gets the better treatment: the exam file usually travels
 * without the pictures it was converted from. Saying "the original question contained an image"
 * and then describing it keeps the question answerable, which is the entire point of requiring a
 * description in the schema.
 */
export function Media({ media, lang }: { readonly media: MediaRef; readonly lang: string }) {
  const { t } = useI18n();

  // A Blob URL rather than a data: URI, so the bytes are not duplicated as base64 in the DOM.
  // Derived during render and released on unmount: an effect that set it into state instead would
  // render the figure once without its image, for no benefit.
  const url = useMemo(
    () =>
      media.payload === undefined
        ? null
        : URL.createObjectURL(
            new Blob([media.payload.bytes], {
              ...(media.mimeType !== undefined && { type: media.mimeType }),
            }),
          ),
    [media],
  );

  useEffect(
    () => () => {
      if (url !== null) {
        URL.revokeObjectURL(url);
      }
    },
    [url],
  );

  if (media.availability === 'embedded' && url !== null) {
    return (
      <figure className="media">
        {/* Always an <img>: never <object>, <embed> or inline SVG, which can execute. */}
        <img src={url} alt={media.alt ?? ''} className="media__image" />
        {media.description !== undefined && (
          <figcaption className="media__caption">
            <ExamText lang={lang}>{media.description}</ExamText>
          </figcaption>
        )}
      </figure>
    );
  }

  if (media.role === 'decorative') {
    return null;
  }

  return (
    <div className="media media--missing">
      <p className="media__notice">{t('media.wasImage')}</p>
      <p className="media__reason">
        {media.unresolvedFrom === 'remote' ? t('media.remote') : t('media.unavailable')}
      </p>
      {media.alt !== undefined && (
        <ExamText lang={lang} className="media__alt">
          {media.alt}
        </ExamText>
      )}
      {media.description !== undefined && (
        <ExamText lang={lang} className="media__description">
          {media.description}
        </ExamText>
      )}
    </div>
  );
}

/** An option's text, its letter, and any image it carries. */
export function OptionLabel({
  option,
  index,
  lang,
}: {
  readonly option: Option;
  readonly index: number;
  readonly lang: string;
}) {
  return (
    <span className="option__label">
      <span className="option__letter" aria-hidden="true">
        {letterFor(index)}
      </span>
      <span className="option__text">
        <ExamText as="span" lang={lang}>
          {option.text}
        </ExamText>
        {option.media.map((media) => (
          <Media key={media.id} media={media} lang={lang} />
        ))}
      </span>
    </span>
  );
}

export interface RendererProps {
  readonly question: CanonicalQuestion;
  /** The option order this session drew, or undefined to show them as authored. */
  readonly optionOrder: readonly string[] | undefined;
  readonly response: Response | null;
  readonly onChange: (response: Response | null) => void;
  readonly disabled: boolean;
  /** Which options are correct, shown only once feedback has been revealed. */
  readonly revealed: boolean;
}

export type OptionState = 'neutral' | 'correct' | 'incorrect' | 'missed';

/** How an option should look once the answer has been revealed. */
export function optionState(
  optionId: OptionId,
  correctIds: readonly OptionId[],
  chosen: readonly OptionId[],
  revealed: boolean,
): OptionState {
  if (!revealed) {
    return 'neutral';
  }
  const isCorrect = correctIds.includes(optionId);
  const wasChosen = chosen.includes(optionId);

  if (isCorrect && wasChosen) {
    return 'correct';
  }
  if (isCorrect) {
    return 'missed';
  }
  return wasChosen ? 'incorrect' : 'neutral';
}
